// The bottom "explore dock": iteration controls, the trace timeline
// (play / replay / drawn portion / speed) and zoom tools.
import type { App } from '../app.ts';
import type { AppState, CurveMeta, ExportMeta } from '../types.ts';
import { $, setOptions } from './dom.ts';

const PLAY_PATH = 'm9 5 10 7-10 7z';
const PAUSE_PATH = 'M8 5v14m8-14v14';

/** "Iteration 8", plus the variant when a curve mixes drawing styles (Penrose d10 · simple). */
export function iterationLabel(e: ExportMeta, curve: CurveMeta): string {
  const variant = (x: ExportMeta) => x.label.split('·')[1]?.trim() ?? '';
  const mine = variant(e);
  const counts = new Map<string, number>();
  for (const x of curve.exports) counts.set(variant(x), (counts.get(variant(x)) ?? 0) + 1);
  let common = '', best = -1;
  for (const [v, n] of counts) if (n > best) { best = n; common = v; }
  return 'Iteration ' + e.depth + (mine && mine !== common ? ' · ' + mine : '');
}

export function mountDock(app: App): void {
  const selExport = $<HTMLSelectElement>('selExport');
  const depthDown = $<HTMLButtonElement>('depthDown');
  const depthUp = $<HTMLButtonElement>('depthUp');
  const depthScrub = $<HTMLInputElement>('depthScrub');
  const depthCount = $('depthCount');
  const autoNext = $('autoNext');
  const play = $<HTMLButtonElement>('play');
  const playIcon = $('playIcon').querySelector('path')!;
  const replay = $<HTMLButtonElement>('replay');
  const rngEnd = $<HTMLInputElement>('rngEnd');
  const lblEnd = $('lblEnd');
  const speed = $<HTMLSelectElement>('speed');
  const zoomLabel = $('zoomLabel');

  let exportsFor = '';
  const syncState = (s: AppState) => {
    const curve = app.curve;
    const list = app.variantExports();
    const key = s.curve + '|' + app.currentVariant();
    if (exportsFor !== key) {
      exportsFor = key;
      setOptions(selExport, list.map((e) => ({ value: e.id, label: 'Iteration ' + e.depth })));
      depthScrub.min = '0';
      depthScrub.max = String(list.length - 1);
      const depths = list.map((e) => e.depth);
      depthCount.textContent = Math.min(...depths) + '–' + Math.max(...depths);
    }
    const i = app.exportIndex();
    selExport.value = app.exp.id;
    if (document.activeElement !== depthScrub) depthScrub.value = String(i);
    depthScrub.setAttribute('aria-valuetext', iterationLabel(app.exp, curve) + ' of ' + list.length);
    depthDown.disabled = i <= 0;
    depthUp.disabled = i >= list.length - 1;
  };
  const syncLive = () => {
    const p = app.player;
    playIcon.setAttribute('d', p.playing ? PAUSE_PATH : PLAY_PATH);
    play.title = p.playing ? 'Pause · Space' : 'Play drawing · Space';
    play.setAttribute('aria-label', p.playing ? 'Pause drawing' : 'Animate drawing');
    autoNext.setAttribute('aria-pressed', String(p.autoNext));
    const end = Math.round(app.range.end * 1000) / 10;
    if (document.activeElement !== rngEnd || p.playing) rngEnd.value = String(end);
    lblEnd.textContent = end + '%';
    speed.value = String(p.speed);
    const z = app.zoomLevel() * 100;
    zoomLabel.textContent = (z >= 1000 ? Math.round(z).toLocaleString() : z.toFixed(z < 10 ? 1 : 0)) + '%';
    const busy = app.stage === 'loading' || !app.geom;
    play.disabled = busy && !p.playing;
    replay.disabled = busy;
  };

  selExport.addEventListener('change', () => app.store.patch({ exportId: selExport.value }));
  depthDown.addEventListener('click', () => app.stepExport(-1));
  depthUp.addEventListener('click', () => app.stepExport(+1));
  depthScrub.addEventListener('input', () => app.selectExportIndex(parseInt(depthScrub.value, 10)));
  autoNext.addEventListener('click', () => app.setAutoNext(!app.player.autoNext));
  play.addEventListener('click', () => app.togglePlay());
  replay.addEventListener('click', () => app.replay());
  rngEnd.addEventListener('input', () => {
    app.pause();
    const v = parseFloat(rngEnd.value) / 100;
    app.setRange(Math.min(app.range.start, Math.max(0, v - 0.001)), v);
  });
  speed.addEventListener('change', () => app.setSpeed(parseFloat(speed.value)));
  $('zoomIn').addEventListener('click', () => app.zoomBy(1.5));
  $('zoomOut').addEventListener('click', () => app.zoomBy(1 / 1.5));
  $('fit').addEventListener('click', () => app.fitView());

  app.store.subscribe(syncState);
  app.listeners.add(syncLive);
  syncState(app.state);
  syncLive();
}
